import React from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { C } from "../common/theme";
import { EmptyState, ErrorState, LoadingRows } from "./States";
import { useIsNarrow } from "./hooks";

export interface Column<T> {
  key: string;
  header: React.ReactNode;
  render: (row: T) => React.ReactNode;
  /** Makes the column sortable (client side) */
  sortValue?: (row: T) => string | number | null | undefined;
  align?: "left" | "right" | "center";
  width?: number | string;
  minWidth?: number;
  /** Hide the column below this width in px (the phone card still shows it unless `card` says otherwise) */
  hideBelow?: number;
  /** Where the column goes in the phone card: the bold first line, the line
   * under it, the right-hand value, a labelled field (default) or nowhere */
  card?: "title" | "subtitle" | "aside" | "field" | "hidden";
  /** Label in the phone card (defaults to header when it's a string) */
  cardLabel?: string;
}

export interface DataTableProps<T> {
  rows: T[];
  columns: Column<T>[];
  getRowId: (row: T) => string;
  loading?: boolean;
  error?: string | null;
  onRetry?: () => void;
  empty?: React.ReactNode;
  onRowClick?: (row: T) => void;
  /** Highlights the open row */
  selectedId?: string | null;
  initialSort?: { key: string; dir: "asc" | "desc" };
  /** Render the phone card yourself instead of from `card` hints */
  renderCard?: (row: T) => React.ReactNode;
  /** Width in px below which rows become cards (default 760) */
  cardBelow?: number;
  /** Extra content under the rows (e.g. a Load more button) */
  footer?: React.ReactNode;
  /** Optional row-level tint (e.g. warning rows) */
  rowTone?: (row: T) => "warning" | "danger" | undefined;
  "aria-label"?: string;
  dense?: boolean;
  /** Tighter rows (about 34px) for long live lists */
  compact?: boolean;
  /** Fixed height: rows scroll inside the box with the header pinned; the
   * footer scrolls with them so "load more" triggers at the bottom */
  height?: number | string | { xs?: number | string; md?: number | string };
  /** Hide the scrollbar of the fixed-height box (it still scrolls) */
  hideScrollbar?: boolean;
  /** The scrolling element when `height` is set (e.g. to keep the reader's place) */
  scrollRef?: React.Ref<HTMLDivElement>;
}

const hiddenScrollbar = { scrollbarWidth: "none" as const, msOverflowStyle: "none", "&::-webkit-scrollbar": { display: "none" } };

const toneBg = { warning: C.bone1, danger: C.dangerTint } as const;

/** The one table: sortable headers, sticky head, row click, loading/empty/
 * error states, and on phones a stack of cards built from column hints. */
export default function DataTable<T>(props: DataTableProps<T>) {
  const {
    rows,
    columns,
    getRowId,
    loading,
    error,
    onRetry,
    empty,
    onRowClick,
    selectedId,
    initialSort,
    renderCard,
    cardBelow = 760,
    footer,
    rowTone,
    dense,
    compact,
    height,
    hideScrollbar,
    scrollRef,
  } = props;
  const boxSx = height ? { height, overflowY: "auto" as const, overscrollBehavior: "contain", ...(hideScrollbar ? hiddenScrollbar : {}) } : {};
  const [sort, setSort] = React.useState(initialSort ?? null);
  const narrow = useIsNarrow(cardBelow);
  const [width, setWidth] = React.useState(() => (typeof window !== "undefined" ? window.innerWidth : 1280));
  React.useEffect(() => {
    const on = () => setWidth(window.innerWidth);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  const sorted = React.useMemo(() => {
    if (!sort) return rows;
    const col = columns.find((c) => c.key === sort.key);
    if (!col?.sortValue) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = col.sortValue!(a);
      const vb = col.sortValue!(b);
      if (va === vb) return 0;
      if (va === null || va === undefined) return 1;
      if (vb === null || vb === undefined) return -1;
      return (va > vb ? 1 : -1) * dir;
    });
  }, [rows, columns, sort]);

  const frame = (content: React.ReactNode) => (
    <Box sx={{ bgcolor: C.paper, border: `1px solid ${C.line}`, borderRadius: "8px", overflow: "hidden", minWidth: 0 }}>{content}</Box>
  );

  if (error && !rows.length) return frame(<ErrorState message={error} onRetry={onRetry} />);
  if (loading && !rows.length) return frame(<LoadingRows />);
  if (!rows.length) return frame(typeof empty === "string" || !empty ? <EmptyState title={empty ?? "Nothing here yet."} /> : empty);

  if (narrow) {
    return (
      <Box ref={height ? scrollRef : undefined} sx={{ display: "flex", flexDirection: "column", gap: compact ? 0.75 : 1, ...boxSx }} aria-label={props["aria-label"]}>
        {sorted.map((row) => {
          const id = getRowId(row);
          const tone = rowTone?.(row);
          const clickable = !!onRowClick;
          return (
            <Box
              key={id}
              component={clickable ? "button" : "div"}
              type={clickable ? "button" : undefined}
              onClick={clickable ? () => onRowClick!(row) : undefined}
              sx={{
                all: clickable ? "unset" : undefined,
                boxSizing: "border-box",
                display: "block",
                width: "100%",
                cursor: clickable ? "pointer" : "default",
                bgcolor: tone ? toneBg[tone] : C.paper,
                border: `1px solid ${selectedId === id ? C.ink : C.line}`,
                borderRadius: "8px",
                p: compact ? 1.25 : 1.5,
                flex: "none",
                "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: 2 },
              }}
            >
              {renderCard ? renderCard(row) : <AutoCard row={row} columns={columns} chevron={clickable} />}
            </Box>
          );
        })}
        {footer}
      </Box>
    );
  }

  const visible = columns.filter((c) => !c.hideBelow || width >= c.hideBelow);
  return frame(
    <>
      <TableContainer ref={height ? scrollRef : undefined} sx={{ overflowX: "auto", ...boxSx }}>
        <Table
          size={dense || compact ? "small" : "medium"}
          stickyHeader
          aria-label={props["aria-label"]}
          sx={compact ? { "& td": { py: "6px", px: "10px", fontSize: 13, lineHeight: 1.35 }, "& th": { py: "7px", px: "10px" } } : undefined}
        >
          <TableHead>
            <TableRow>
              {visible.map((col) => (
                <TableCell key={col.key} align={col.align} sx={{ width: col.width, minWidth: col.minWidth }}>
                  {col.sortValue ? (
                    <TableSortLabel
                      active={sort?.key === col.key}
                      direction={sort?.key === col.key ? sort.dir : "desc"}
                      onClick={() =>
                        setSort((s) => (s?.key === col.key ? { key: col.key, dir: s.dir === "asc" ? "desc" : "asc" } : { key: col.key, dir: "desc" }))
                      }
                    >
                      {col.header}
                    </TableSortLabel>
                  ) : (
                    col.header
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((row) => {
              const id = getRowId(row);
              const tone = rowTone?.(row);
              return (
                <TableRow
                  key={id}
                  hover={!!onRowClick}
                  selected={selectedId === id}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  tabIndex={onRowClick ? 0 : undefined}
                  onKeyDown={
                    onRowClick
                      ? (e) => {
                          if (e.key === "Enter") onRowClick(row);
                        }
                      : undefined
                  }
                  sx={{
                    cursor: onRowClick ? "pointer" : "default",
                    bgcolor: tone ? toneBg[tone] : undefined,
                    "&:focus-visible": { outline: `2px solid ${C.coral}`, outlineOffset: -2 },
                  }}
                >
                  {visible.map((col) => (
                    <TableCell key={col.key} align={col.align} sx={{ verticalAlign: "top" }}>
                      {col.render(row)}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {footer && height && <Box sx={{ borderTop: `1px solid ${C.line}` }}>{footer}</Box>}
      </TableContainer>
      {footer && !height && <Box sx={{ borderTop: `1px solid ${C.line}` }}>{footer}</Box>}
    </>
  );
}

function AutoCard<T>({ row, columns, chevron }: { row: T; columns: Column<T>[]; chevron: boolean }) {
  const pick = (kind: Column<T>["card"]) => columns.filter((c) => (c.card ?? "field") === kind);
  const [title] = pick("title");
  const subtitles = pick("subtitle");
  const asides = pick("aside");
  const fields = pick("field");
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          {title && <Box sx={{ fontWeight: 700, fontSize: 14.5, overflowWrap: "anywhere" }}>{title.render(row)}</Box>}
          {subtitles.map((c) => (
            <Box key={c.key} sx={{ fontSize: 12.5, color: C.muted, mt: 0.25 }}>
              {c.render(row)}
            </Box>
          ))}
        </Box>
        {asides.length > 0 && (
          <Box sx={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
            {asides.map((c) => (
              <Box key={c.key}>{c.render(row)}</Box>
            ))}
          </Box>
        )}
        {chevron && <ChevronRightIcon sx={{ color: C.faint, mt: 0.25, mr: -0.5 }} />}
      </Box>
      {fields.length > 0 && (
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "8px 12px" }}>
          {fields.map((c) => (
            <Box key={c.key} sx={{ minWidth: 0 }}>
              <Typography variant="overline" component="div" sx={{ color: C.muted, lineHeight: 1.4 }}>
                {c.cardLabel ?? (typeof c.header === "string" ? c.header : c.key)}
              </Typography>
              <Box sx={{ fontSize: 13.5, overflowWrap: "anywhere" }}>{c.render(row)}</Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}
